import { api } from "@Aspecto/backend/convex/_generated/api";
import type { Id } from "@Aspecto/backend/convex/_generated/dataModel";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { useQuery, useMutation } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Pencil, Plus, Tag, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SectionCard } from "@/components/ui/section-card";

export const Route = createFileRoute("/_authenticated/app/admin/tags")({
  component: TagsSettings,
});

function TagsSettings() {
  const { data: tags } = useQuery(convexQuery(api.tags.getTagsWithUsageCounts, {}));

  const { mutateAsync: createTag } = useMutation({
    mutationFn: useConvexMutation(api.tags.createTag),
  });
  const { mutateAsync: renameTag } = useMutation({
    mutationFn: useConvexMutation(api.tags.renameTag),
  });
  const { mutateAsync: deleteTag } = useMutation({
    mutationFn: useConvexMutation(api.tags.deleteTag),
  });

  const [createOpen, setCreateOpen] = useState(false);
  const [createName, setCreateName] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  const [renameId, setRenameId] = useState<Id<"tags"> | null>(null);
  const [renameName, setRenameName] = useState("");
  const [isRenaming, setIsRenaming] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<{
    id: Id<"tags">;
    name: string;
    usageCount: number;
  } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleCreate = async () => {
    if (!createName.trim()) return;
    setIsCreating(true);
    try {
      await createTag({ name: createName.trim() });
      toast.success(`Tag "${createName.trim()}" created`);
      setCreateName("");
      setCreateOpen(false);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to create tag");
    } finally {
      setIsCreating(false);
    }
  };

  const handleRename = async () => {
    if (!renameId || !renameName.trim()) return;
    setIsRenaming(true);
    try {
      await renameTag({ tagId: renameId, name: renameName.trim() });
      toast.success("Tag renamed");
      setRenameId(null);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to rename tag");
    } finally {
      setIsRenaming(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await deleteTag({ tagId: deleteTarget.id });
      toast.success(`Tag "${deleteTarget.name}" deleted`);
      setDeleteTarget(null);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to delete tag");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <SectionCard
        title="Tags"
        description="Create and manage tags that can be applied to communities, events, and posts."
        action={
          <Button size="sm" onClick={() => setCreateOpen(true)}>
            <Plus className="mr-1.5 h-4 w-4" />
            Create tag
          </Button>
        }
      >
        {!tags || tags.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-8 text-center text-muted-foreground">
            <Tag className="h-8 w-8 opacity-40" />
            <p className="text-sm">No tags yet. Create your first tag to get started.</p>
          </div>
        ) : (
          <div className="divide-y">
            {tags.map((tag) => (
              <div key={tag._id} className="flex items-center justify-between gap-3 py-3">
                <div className="flex min-w-0 items-center gap-3">
                  <Badge variant="secondary">{tag.name}</Badge>
                  <span className="text-xs text-muted-foreground">
                    {tag.usageCount === 0
                      ? "Not used"
                      : `Used in ${tag.usageCount} ${tag.usageCount === 1 ? "item" : "items"}`}
                  </span>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setRenameId(tag._id);
                      setRenameName(tag.name);
                    }}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-destructive hover:text-destructive"
                    onClick={() =>
                      setDeleteTarget({ id: tag._id, name: tag.name, usageCount: tag.usageCount })
                    }
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </SectionCard>

      {/* Create tag dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create tag</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="tag-name">Tag name</Label>
            <Input
              id="tag-name"
              value={createName}
              onChange={(e) => setCreateName(e.target.value)}
              placeholder="e.g. Engineering"
              onKeyDown={(e) => e.key === "Enter" && void handleCreate()}
              disabled={isCreating}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} disabled={isCreating}>
              Cancel
            </Button>
            <Button onClick={() => void handleCreate()} disabled={!createName.trim() || isCreating}>
              {isCreating ? "Creating…" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rename tag dialog */}
      <Dialog open={!!renameId} onOpenChange={(o) => !o && setRenameId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename tag</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="rename-tag-name">Tag name</Label>
            <Input
              id="rename-tag-name"
              value={renameName}
              onChange={(e) => setRenameName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && void handleRename()}
              disabled={isRenaming}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameId(null)} disabled={isRenaming}>
              Cancel
            </Button>
            <Button onClick={() => void handleRename()} disabled={!renameName.trim() || isRenaming}>
              {isRenaming ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete tag "{deleteTarget?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget && deleteTarget.usageCount > 0
                ? `This tag is used in ${deleteTarget.usageCount} ${deleteTarget.usageCount === 1 ? "item" : "items"}. Deleting it will remove it from all associated content.`
                : "This tag is not currently used."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)} disabled={isDeleting}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={() => void handleDelete()} disabled={isDeleting}>
              {isDeleting ? "Deleting…" : "Delete"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
